/**
 * RequirementsChecklist Component
 *
 * Displays guide's approval requirements in a checklist format.
 * Shows status, priority, due dates, and review notes for each requirement.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-150 - Guide Dashboard Enhancements (13 SP)
 *
 * Props:
 * - requirements: array of requirement objects
 * - loading: boolean
 * - compact: boolean - if true, show simplified view
 */

import {
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  XCircle
} from 'lucide-react';
import StatusBadge from '../common/StatusBadge';
import Badge from '../common/Badge';
import Alert from '../common/Alert';

/**
 * Map status to StatusBadge variant
 */
function getStatusVariant(status) {
  const variants = {
    pending: 'warning',
    submitted: 'info',
    reviewing: 'info',
    approved: 'success',
    rejected: 'error',
    waived: 'neutral',
  };
  // eslint-disable-next-line security/detect-object-injection -- safe: status is enum from database
  return variants[status] || 'neutral';
}

/**
 * Map priority to Badge color
 */
function getPriorityColor(priority) {
  const colors = {
    low: 'gray',
    medium: 'blue',
    high: 'orange',
    urgent: 'red',
  };
  // eslint-disable-next-line security/detect-object-injection -- safe: priority is enum from database
  return colors[priority] || 'gray';
}

/**
 * Get status icon
 */
function getStatusIcon(status) {
  const icons = {
    pending: Clock,
    submitted: FileText,
    reviewing: Clock,
    approved: CheckCircle,
    rejected: XCircle,
    waived: CheckCircle,
  };
  // eslint-disable-next-line security/detect-object-injection -- safe: status is enum from database
  return icons[status] || Clock;
}

/**
 * Format requirement type for display
 */
function formatType(type) {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format status for display
 */
function formatStatus(status) {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export default function RequirementsChecklist({ requirements, loading = false, compact = false }) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!requirements || requirements.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 mb-1">All Requirements Complete!</h3>
        <p className="text-sm text-gray-600">
          You have no pending approval requirements at this time.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          Approval Requirements ({requirements.length})
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Complete these requirements to proceed with your guide profile approval
        </p>
      </div>

      {/* Requirements list */}
      <div className="divide-y divide-gray-200">
        {requirements.map((req) => {
          const StatusIcon = getStatusIcon(req.status);
          const isOverdue = req.due_date && new Date(req.due_date) < new Date();
          const isPending = ['pending', 'submitted'].includes(req.status);
          const isComplete = ['approved', 'waived'].includes(req.status);

          return (
            <div
              key={req.id}
              className={`p-6 ${isOverdue && isPending ? 'bg-red-50' : ''} ${isComplete ? 'opacity-75' : ''}`}
            >
              <div className="flex items-start gap-4">
                {/* Status icon */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  isComplete ? 'bg-green-100' : isOverdue && isPending ? 'bg-red-100' : 'bg-gray-100'
                }`}>
                  <StatusIcon className={`w-5 h-5 ${
                    isComplete ? 'text-green-600' : isOverdue && isPending ? 'text-red-600' : 'text-gray-600'
                  }`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-gray-900 mb-1">
                        {req.title}
                      </h4>
                      {req.description && !compact && (
                        <p className="text-sm text-gray-600 mb-2">{req.description}</p>
                      )}
                    </div>

                    {/* Status badge */}
                    <StatusBadge variant={getStatusVariant(req.status)}>
                      {formatStatus(req.status)}
                    </StatusBadge>
                  </div>

                  {/* Metadata */}
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-2">
                    {/* Priority */}
                    <Badge color={getPriorityColor(req.priority)}>
                      {req.priority === 'urgent' && <AlertTriangle className="w-3 h-3 mr-1" />}
                      {req.priority.toUpperCase()} PRIORITY
                    </Badge>

                    {/* Type */}
                    <span className="flex items-center">
                      Type: <span className="ml-1 font-medium text-gray-700">{formatType(req.type)}</span>
                    </span>

                    {/* Due date */}
                    {req.due_date && (
                      <span className={`flex items-center ${isOverdue && isPending ? 'text-red-600 font-semibold' : ''}`}>
                        <Clock className="w-3 h-3 mr-1" />
                        Due: {new Date(req.due_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                        {isOverdue && isPending && ' (Overdue)'}
                      </span>
                    )}

                    {/* Reviewed by */}
                    {req.reviewed_by_name && (
                      <span>
                        Reviewed by: <span className="font-medium text-gray-700">{req.reviewed_by_name}</span>
                      </span>
                    )}
                  </div>

                  {/* Review notes */}
                  {req.review_notes && !compact && (
                    <Alert variant="info" className="mt-3">
                      <p className="text-xs font-semibold mb-1">Review Notes:</p>
                      <p className="text-sm">{req.review_notes}</p>
                    </Alert>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
